import React from "react";
import VideoBlogLayout from "../../components/VideoBlogLayout";

const BlogAIClaimSubmissionSkills = () => (
  <VideoBlogLayout
    category="Video Demo"
    eyebrow="AI Claim Submission + Skills"
    title="How Asha's AI submits claims on its own — and how you train it with skills"
    subtitle="A look at the reason-and-act loop the AI runs to submit claims without human review, why we hold autonomy at 65–75% on purpose, and the plain-English Skills feature that lets you personalize how the AI handles any payer or procedure."
    author="Asha Team"
    date="April 24, 2026"
    readTime="4 min watch"
    videoUrl="https://www.loom.com/embed/4ce36bd494124615875cac3b3cd17057"
    videoTitle="AI Claim Submission and Skills in the Asha AI Operating System"
    videoProvider="loom"
  >
    <div className="space-y-14">
      <div>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          Asha's AI automatically submits claims the day after a procedure —
          no human in the loop required, in most cases. But the interesting
          part isn't the speed. It's{" "}
          <strong className="text-gray-900 font-semibold">
            how the AI thinks through the claim
          </strong>{" "}
          before firing it off, and how you can teach it new behaviors in
          plain English with a feature called Skills.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          Reason in loops, act in loops
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          When AI picks up a fresh claim, it doesn't follow a static rule
          list. It reads the patient's tooth chart, perio chart data, clinical
          notes, appointment history, and benefits — then loops between
          reasoning and taking action:
        </p>
        <ul className="space-y-3 text-lg text-gray-600 leading-relaxed">
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>
              <strong className="text-gray-900 font-semibold">
                Coding review
              </strong>{" "}
              — checking nuances like downcoding and bundling.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>
              <strong className="text-gray-900 font-semibold">
                Clinical audit
              </strong>{" "}
              — making sure documentation actually supports what's being
              billed.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>
              <strong className="text-gray-900 font-semibold">
                Payer-specific denial pattern lookup
              </strong>{" "}
              — "for this payer and these codes, we've seen denials — what's
              the best information to attach to give this claim the best
              shot?"
            </span>
          </li>
        </ul>
        <p className="text-lg text-gray-600 leading-relaxed mt-4">
          It keeps looping until it's confident. Before it commits, a{" "}
          <strong className="text-gray-900 font-semibold">
            devil's-advocate AI agent
          </strong>{" "}
          challenges its reasoning. Only when it's fully confident does it
          validate the claim with the clearing house and submit — narrative
          written, procedures attached, provider info filled, images pulled
          from your sensor, all without your staff opening it.
        </p>
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-6">
        <h3 className="text-lg font-bold text-blue-900 mb-2">
          The 65–75% rule (and why we don't push it higher)
        </h3>
        <p className="text-base text-gray-700 leading-relaxed mb-3">
          For most practices, the AI submits{" "}
          <strong className="text-gray-900 font-semibold">65–75%</strong> of
          claims fully autonomously — narrative, attachments, codes, sent.
          The remaining 25–35% still get all the prep done, but are flagged
          for your staff to glance over.
        </p>
        <p className="text-base text-gray-700 leading-relaxed">
          We hold that line on purpose. We care about{" "}
          <strong className="text-gray-900 font-semibold">
            close to zero false positives
          </strong>{" "}
          — a wrong claim going out without review is worse than a correct
          claim being flagged unnecessarily. False negatives (correct work,
          staff still reviews) are an acceptable trade for that guarantee.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          Skills — train the AI like you'd train staff
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          Buried in settings is one of the most powerful features in the
          platform:{" "}
          <strong className="text-gray-900 font-semibold">Skills</strong>.
          When you find a payer-and-procedure combination that needs to be
          billed a particular way, you write the instruction in plain English
          and pick which parts of the revenue cycle it applies to.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed">
          Example: "Whenever we submit an SRP claim to Blue Cross Blue
          Shield, always include the following in the narrative…" Save that
          skill, and from then on the AI follows it on every BCBS SRP claim
          — no engineer required.
        </p>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          Skills apply across the whole revenue cycle
        </h2>
        <p className="text-lg text-gray-600 leading-relaxed mb-4">
          The same Skills system extends well past claim submission. You can
          write skills that change how the AI handles:
        </p>
        <ul className="space-y-3 text-lg text-gray-600 leading-relaxed">
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>EOB posting — what to flag, when to auto-post.</span>
          </li>
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>Bank reconciliation — how to match tricky payer memos.</span>
          </li>
          <li className="flex gap-3">
            <span className="text-blue-600 mt-1.5 flex-shrink-0">•</span>
            <span>Adjustment calculations — payer- and provider-level nuance.</span>
          </li>
        </ul>
        <p className="text-lg text-gray-600 leading-relaxed mt-4">
          It's the same idea as training a new hire — explain the rule in
          plain English once, and the AI applies it from there on.
        </p>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">
          Take it as far as you want
        </h3>
        <p className="text-base text-gray-700 leading-relaxed">
          You can stick with the defaults and the AI will still handle the
          vast majority of claims on its own. But Skills lets you push it
          further — encoding the institutional knowledge your best biller has
          built up over years, so the next denial that came back yesterday
          doesn't come back tomorrow.
        </p>
      </div>
    </div>
  </VideoBlogLayout>
);

export default BlogAIClaimSubmissionSkills;
